import { useState } from "react";
import { EDIT_GROUPS, type EditField } from "../memberEditConfig";
import type { MatchProfile } from "../adminConfig";
import PhotoUploader from "./PhotoUploader";

export type MemberFormPayload = Record<string, string | number | null>;

interface MemberFormProps {
  initial?: Partial<MatchProfile>;
  submitLabel: string;
  submittingLabel?: string;
  onSubmit: (payload: MemberFormPayload) => Promise<void>;
  onCancel: () => void;
  cancelLabel?: string;
}

type FormState = Record<string, string>;

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-300 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100";

function buildInitialState(initial?: Partial<MatchProfile>): FormState {
  const state: FormState = { photo_url: initial?.photo_url ?? "" };
  EDIT_GROUPS.forEach((group) => {
    group.fields.forEach((field) => {
      const value = initial ? initial[field.key] : null;
      state[field.key as string] = value === null || value === undefined ? "" : String(value);
    });
  });
  return state;
}

export default function MemberForm({
  initial,
  submitLabel,
  submittingLabel = "保存中…",
  onSubmit,
  onCancel,
  cancelLabel = "取消",
}: MemberFormProps) {
  const [form, setForm] = useState<FormState>(() => buildInitialState(initial));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (error) setError("");
  };

  const renderField = (field: EditField) => {
    const value = form[field.key as string] ?? "";

    if (field.type === "select") {
      const options = field.options || [];
      const isCustom = value !== "" && !options.includes(value);
      return (
        <select
          value={value}
          onChange={(e) => handleChange(field.key as string, e.target.value)}
          className={[inputClass, value ? "" : "text-foreground-400"].join(" ")}
        >
          <option value="">请选择</option>
          {isCustom && <option value={value}>{value}（原填写值）</option>}
          {options.map((option) => (
            <option key={option} value={option} className="text-foreground-900">
              {option}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === "textarea") {
      return (
        <textarea
          rows={3}
          value={value}
          placeholder={field.placeholder}
          onChange={(e) => handleChange(field.key as string, e.target.value)}
          className={[inputClass, "resize-y leading-relaxed"].join(" ")}
        ></textarea>
      );
    }

    return (
      <input
        type={field.type === "number" ? "number" : "text"}
        inputMode={field.type === "number" ? "numeric" : undefined}
        value={value}
        placeholder={field.placeholder}
        onChange={(e) => handleChange(field.key as string, e.target.value)}
        className={inputClass}
      />
    );
  };

  const handleSubmit = async () => {
    const name = (form.name || "").trim();
    const gender = (form.gender || "").trim();
    const phone = (form.phone || "").trim();

    if (!name) {
      setError("姓名不能为空");
      return;
    }
    if (!gender) {
      setError("请选择性别");
      return;
    }
    if (!phone) {
      setError("手机号不能为空");
      return;
    }

    const payload: MemberFormPayload = {};
    EDIT_GROUPS.forEach((group) => {
      group.fields.forEach((field) => {
        const raw = (form[field.key as string] ?? "").trim();
        if (field.type === "number") {
          payload[field.key as string] = raw === "" ? null : Number(raw);
        } else {
          payload[field.key as string] = raw === "" ? null : raw;
        }
      });
    });
    const photo = (form.photo_url || "").trim();
    payload.photo_url = photo === "" ? null : photo;

    setSaving(true);
    setError("");
    try {
      await onSubmit(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存失败，请稍后重试");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3">
        <i className="ri-information-line mt-0.5 text-secondary-600"></i>
        <p className="text-xs leading-relaxed text-secondary-900">
          带 <span className="text-accent-600">*</span> 的为必填项（姓名、性别、手机号）。填好后点击底部
          「{submitLabel}」即可。
        </p>
      </div>

      <section className="mt-4 rounded-lg border border-background-200 bg-background-50">
        <div className="flex items-center gap-2 border-b border-background-200 px-4 py-3">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-100 text-accent-600">
            <i className="ri-image-2-line text-sm"></i>
          </span>
          <h3 className="font-heading text-sm font-semibold text-foreground-900">会员照片</h3>
          <span className="text-xs text-foreground-400">（选填）</span>
        </div>
        <div className="px-4 py-4">
          <PhotoUploader
            value={form.photo_url || ""}
            onChange={(url) => handleChange("photo_url", url)}
          />
        </div>
      </section>

      {EDIT_GROUPS.map((group) => (
        <section key={group.title} className="mt-4 rounded-lg border border-background-200 bg-background-50">
          <div className="flex items-center gap-2 border-b border-background-200 px-4 py-3">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-600">
              <i className={`${group.icon} text-sm`}></i>
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-900">{group.title}</h3>
          </div>
          <div className="grid grid-cols-1 gap-x-5 gap-y-4 px-4 py-4 sm:grid-cols-2">
            {group.fields.map((field) => (
              <div key={field.key as string} className={field.full ? "sm:col-span-2" : ""}>
                <label className="mb-1.5 block text-xs font-medium text-foreground-600">
                  {field.label}
                  {field.required && <span className="ml-0.5 text-accent-600">*</span>}
                </label>
                {renderField(field)}
              </div>
            ))}
          </div>
        </section>
      ))}

      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-3">
          <i className="ri-error-warning-line mt-0.5 text-accent-600"></i>
          <p className="text-xs leading-relaxed text-accent-800">{error}</p>
        </div>
      )}

      <div className="sticky bottom-0 -mx-5 -mb-5 mt-4 flex flex-col gap-2 border-t border-background-200 bg-background-100/95 px-5 py-4 backdrop-blur sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving}
          className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-3-line"></i>}
          {saving ? submittingLabel : submitLabel}
        </button>
      </div>
    </div>
  );
}